import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';

import { combineLatest, map, switchMap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { ProtectedControlDirective } from '@core/user-interface-legacy';
import type {
  AnswerComponentConfig,
  DynamicFormAnswerConfig,
  DynamicFormConfig,
  RespondFormState,
} from '@tellent-ask-hr/dynamic-forms-types';

import { DynamicFormConfigService } from '../../dynamic-form-config.service';

@Component({
  selector: 'dynamic-form-respond',
  template: `
    <div class="respond-form-list">
      @for (cmp of formComponents(); track cmp.hash) {
        <ng-container [rtProtectedControl]="cmp.inputs.respondForm" />
        <ng-template [ngComponentOutlet]="cmp.component" [ngComponentOutletInputs]="cmp.inputs" />
      }
    </div>
  `,
  styleUrl: './dynamic-form-respond.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, ProtectedControlDirective],
  providers: [DynamicFormConfigService],
})
export class DynamicFormRespondComponent {
  private readonly dynamicFormConfigService = inject(DynamicFormConfigService);
  protected readonly formComponents = signal<AnswerComponentConfig[]>([]);
  protected readonly formState$ = toObservable(this.formComponents).pipe(
    switchMap(configs =>
      combineLatest(
        configs.map(config =>
          config.inputs.respondForm.valueChanges.pipe(
            startWith(config.inputs.respondForm.getRawValue()),
            map(
              value =>
                <RespondFormState<DynamicFormConfig>>{
                  kind: config.kind,
                  hash: config.hash,
                  value,
                },
            ),
          ),
        ),
      ),
    ),
  );
  protected readonly formStatus$ = toObservable(this.formComponents).pipe(
    switchMap(configs =>
      combineLatest(
        configs.map(config => {
          const { statusChanges, status } = config.inputs.respondForm;
          return statusChanges.pipe(startWith(status));
        }),
      ),
    ),
  );

  public formConfig = input.required<DynamicFormAnswerConfig[]>();
  public formStateChanged = outputFromObservable(this.formState$);
  public formStatusChanged = outputFromObservable(this.formStatus$);

  private initForm = effect(() => {
    const respondConfigs = this.formConfig();
    untracked(() => this.createForm(respondConfigs));
  });

  private async createForm(respondConfigs: DynamicFormAnswerConfig[]) {
    const components = await Promise.all(respondConfigs.map(data => this.createComponent(data)));
    this.formComponents.set(components);
  }

  private async createComponent(config: DynamicFormAnswerConfig): Promise<AnswerComponentConfig> {
    const dynamicFormConfig = this.dynamicFormConfigService.configsMap[config.kind];
    const component = await dynamicFormConfig.components.respond();

    return {
      hash: config.hash,
      kind: config.kind,
      component,
      inputs: {
        respondConfig: dynamicFormConfig.respondConfig(config.config),
        respondForm: dynamicFormConfig.respondForm(config.config),
      },
    };
  }
}
